<template>
  <component
    v-if="canPrompt"
    :is="isListItem ? 'list-item' : 'span'"
    class="w-100"
    @click="() => (isListItem ? addToHomescreen(context) : {})"
  >
    <template v-if="isListItem">
      <template slot="left">
        <v-icon icon="download" />
      </template>
      <div class="v-list-item__title --centered ml-2">
        {{ $t("components.addToHomescreen.downloadApp") }}
      </div>
    </template>

    <v-block-link v-else @click="() => addToHomescreen(context)">
      <img
        class="add-to-homescreen__icon"
        :src="getImageUrl(appIcon)"
        :alt="appName"
      />
      {{ $t("components.addToHomescreen.downloadApp") }}

      <v-icon class="ml-auto mr-2" icon="download" />
    </v-block-link>

    <v-ons-alert-dialog
      cancelable
      @posthide="handleHide"
      :class="[
        {
          '--ios-facebook': isIosBrowser(BROWSERS.FACEBOOK),
          '--ios-instagram': isIosBrowser(BROWSERS.INSTAGRAM),
          '--ios-messenger': isIosBrowser(BROWSERS.MESSENGER),
          '--ios-ipad': isIosSafari && isIpad(),
          '--ios-open-safari': showOpenSafari,
          '--ios-safari': isIosSafari
        },
        'add-to-homescreen__alert'
      ]"
      :visible="isDownloading"
    >
      <div
        v-if="isIosSafari && !showOpenSafari"
        @click="() => (showOpenSafari = true)"
        class="ios-download__safari-prompt"
      >
        {{ $t("components.addToHomescreen.notSeeingPrompt") }}
        <span class="text-underline">{{
          $t("components.addToHomescreen.notSeeingInstruction")
        }}</span>
      </div>
      <span slot="title">
        {{ $t("components.addToHomescreen.installAll", { appName: appName }) }}
      </span>
      <div class="p-relative overflow-hidden">
        <div class="app-icon-row">
          <div class="app-icon --placeholder"></div>
          <div class="app-icon --placeholder"></div>
          <img class="app-icon" :src="getImageUrl(appIcon)" />
          <div class="app-icon --placeholder"></div>
          <div class="app-icon --placeholder"></div>
        </div>
        <div class="app-icon-row__spacer"></div>

        <template v-if="isIos">
          <div class="ios-download">
            <template v-if="!isIosSafari && !isMetaBrowser">
              <div class="text-left px-2">
                <p class="mb-1">{{ $t("components.addToHomescreen.iosStep1") }}</p>
                <copy-button :copy-text="copyUrl" class="mt-1" small block>
                  {{ $t("components.addToHomescreen.iosStep1Subheading") }}
                </copy-button>
                <p class="d-flex align-items-center">
                  {{ $t("components.addToHomescreen.iosStep2") }}
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-compass" v-bind:class="'add-to-homescreen__safari-icon mx-1'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><circle cx="12" cy="12" r="10"/><path d="M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z"/></svg>
                  {{ $t("components.addToHomescreen.iosStep2Subheading") }}
                </p>
                <p class="mb-0">{{ $t("components.addToHomescreen.iosStep3") }}</p>
              </div>
            </template>

            <template v-else>
              {{ $t("components.addToHomescreen.tapInstruction") }}

              <template v-if="!showOpenSafari">
                <template
                  v-if="
                    isIosBrowser(BROWSERS.FACEBOOK) ||
                      isIosBrowser(BROWSERS.INSTAGRAM)
                  "
                >
                  <img
                    class="ios-download__icon"
                    :src="
                      require('@/assets/images/icons/ios-more-facebook.svg')
                    "
                  />
                </template>
                <template v-else>
                  <img
                    class="ios-download__icon"
                    :src="require('@/assets/images/icons/ios-share.svg')"
                  />
                </template>
                {{ $t("components.addToHomescreen.thenInstruction") }}
              </template>
              <template v-if="promptOpenSafari">
                <img
                  class="ios-download__icon --add-to-homescreen"
                  :src="require('@/assets/images/icons/ios-safari.svg')"
                />
                {{ $t("components.addToHomescreen.openInInstruction") }}
                {{
                  isIosBrowser(BROWSERS.FACEBOOK)
                    ? $t("components.addToHomescreen.openInBrowserInstruction")
                    : $t("components.addToHomescreen.openInSafariInstruction")
                }}
              </template>
              <template v-else>
                <img
                  class="ios-download__icon --add-to-homescreen"
                  :src="
                    require('@/assets/images/icons/ios-add-to-homescreen.svg')
                  "
                />

                {{ $t("components.addToHomescreen.addToHomeInstruction") }}
              </template>
            </template>
          </div>
        </template>
        <template v-else>
          <div class="mt-2">
            <v-ons-progress-bar indeterminate></v-ons-progress-bar>
          </div>
        </template>
      </div>
    </v-ons-alert-dialog>
  </component>
</template>

<script>
import DetectInApp from "detect-inapp"
import { mapState, mapGetters, mapActions, mapMutations } from "vuex"

import enumValidator from "@/lib/validators/enum-validator"
import { isIos, isIpad, isSafari } from "@/lib/is-platform"
import { getImageUrl, replaceQueryParam } from "@/lib/utils"

const BROWSERS = {
  FACEBOOK: "facebook",
  INSTAGRAM: "instagram",
  MESSENGER: "messenger",
  SAFARI: "safari"
}
const DOWNLOAD_QUERY_KEY = "download"
const DOWNLOAD_QUERY_CONTEXT = "query_param"

export default {
  components: {
    ListItem: () => import("@/components/ListItem"),
    CopyButton: () => import("@/components/CopyButton")
  },

  props: {
    context: {
      type: String,
      required: true,
      validator: enumValidator([
        "download_bar_home",
        "auth_flow",
        "account",
        "my_content"
      ])
    },
    isListItem: { type: Boolean, default: false }
  },

  mounted() {
    if (DOWNLOAD_QUERY_KEY in this.$route.query && this.isIos) {
      this.downloadContext = DOWNLOAD_QUERY_CONTEXT
      this.addToHomescreen(this.downloadContext)
      setTimeout(() => {
        replaceQueryParam(this.$router, { download: undefined })
      }, 100)
    }
  },

  watch: {
    isDownloading() {
      if (this.isDownloading) {
        replaceQueryParam(this.$router, { download: null })
      } else {
        replaceQueryParam(this.$router, { download: undefined })
      }
    }
  },

  data() {
    return {
      detectInApp: new DetectInApp(
        navigator.userAgent || navigator.vendor || window.opera
      ),
      showOpenSafari: false,
      downloadContext: this.context,
      BROWSERS
    }
  },

  computed: {
    ...mapState(["global"]),
    ...mapState("pwa", ["isDownloading", "hasDownloaded"]),
    ...mapGetters("pwa", ["canPrompt"]),
    ...mapGetters(["appIcon", "appName"]),

    promptOpenSafari() {
      return this.isIos && (this.showOpenSafari || this.isMetaBrowser)
    },

    isMetaBrowser() {
      return [
        BROWSERS.FACEBOOK,
        BROWSERS.INSTAGRAM,
        BROWSERS.MESSENGER
      ].includes(this.browser)
    },

    browser() {
      return this.detectInApp.browser
    },

    isIos() {
      return isIos()
    },

    isIosSafari() {
      return isIos() && isSafari()
    },

    copyUrl() {
      return `${window.location.origin}/?${DOWNLOAD_QUERY_KEY}`
    }
  },

  methods: {
    isIpad,
    getImageUrl,

    ...mapActions("pwa", ["addToHomescreen"]),
    ...mapActions("analytics", ["trackActivity"]),
    ...mapMutations("pwa", ["setIsDownloading"]),

    handleHide() {
      if (!this.hasDownloaded) {
        this.trackActivity({
          activityType: "app_download_aborted",
          data: { context: this.downloadContext }
        })
      }

      this.showOpenSafari = false
      this.setIsDownloading(false)
    },

    isIosBrowser(browser) {
      return this.isIos && this.browser === browser
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

$ios-download--background: #e1e1e1;
$alert-dialog--width: 300px;

.add-to-homescreen__icon {
  height: 53px;
  width: 53px;
  padding: $size--2;
  margin-right: $size--1;
}

.add-to-homescreen__alert {
  .alert-dialog-container {
    border: 6px solid $ios-download--background;
  }

  .alert-dialog.alert-dialog--material {
    width: $alert-dialog--width;
    border-radius: var(--border-radius);
  }

  .alert-dialog-container--material {
    border-radius: var(--border-radius);
  }

  .alert-dialog-title.alert-dialog-title--material {
    text-align: center;
  }

  .alert-dialog-content--material {
    padding: 0;
    margin-bottom: 0;
  }

  .alert-dialog-footer--material {
    height: initial;
  }

  &.--ios-instagram,
  &.--ios-facebook,
  &.--ios-messenger,
  &.--ios-safari {
    .alert-dialog {
      top: 80%;
      transition: transform 0.2s ease;

      &:after {
        content: "";
        position: absolute;
        left: 50%;
        bottom: -12px;
        margin-left: -12px;
        border-left: 12px solid transparent;
        border-right: 12px solid transparent;
        border-top: 12px solid $ios-download--background;
      }
    }
  }

  &.--ios-messenger,
  &.--ios-open-safari {
    .alert-dialog {
      right: 12px;
      left: initial;
      transform: translate(0%, -50%);

      &:after {
        content: "";
        left: auto;
        right: 8px;
      }
    }
  }

  &.--ios-instagram {
    .alert-dialog {
      top: 124px;
      right: 8px;
      left: initial;
      transform: translate(0%, -50%);

      &:after {
        content: "";
        left: auto;
        right: 8px;

        bottom: auto;
        top: -12px;
        border-left: 12px solid transparent;
        border-right: 12px solid transparent;
        border-bottom: 12px solid $ios-download--background;
        border-top: none;
      }
    }
  }

  &.--ios-facebook {
    .alert-dialog {
      top: auto;
      bottom: 0;
      right: 8px;
      left: initial;
      transform: translate(0%, -10%);

      &:after {
        content: "";
        left: auto;
        right: 24px;
        bottom: -12px;
        top: auto;
      }
    }
  }

  &.--ios-ipad {
    .alert-dialog {
      top: 0;
      bottom: auto;
      right: 0;
      left: initial;
      transform: translate(-6px, 8%);

      &:after {
        content: "";
        left: auto;
        right: 12vw;
        bottom: auto;
        top: -12px;
        border-left: 12px solid transparent;
        border-right: 12px solid transparent;
        border-bottom: 12px solid $ios-download--background;
        border-top: none;
      }
    }
  }
}

.app-icon-row {
  display: flex;
  position: absolute;
  width: 340px;
  margin-left: -170px;
  left: 50%;
  top: 10px;
}

.app-icon-row__spacer {
  height: 80px;
}

.add-to-homescreen__safari-icon {
  color: #0279ff;
}

.app-icon {
  height: 64px;
  width: 64px;
  border-radius: 16px;
  box-shadow: $box-shadow--large;
  margin: 0 4px;

  &.--placeholder {
    height: 60px;
    width: 60px;
    box-shadow: none;
    background: $ios-download--background;
    opacity: 0;
    position: relative;
    top: 4px;

    &:first-child {
      animation: fade-in-out 2.4s ease infinite;
      animation-delay: 0.2s;
    }

    &:nth-child(2) {
      animation: fade-in-out 2.4s ease infinite;
      animation-delay: 0.8s;
    }

    &:nth-child(4) {
      animation: fade-in-out 2.4s ease infinite;
      animation-delay: 0.8s;
    }

    &:last-child {
      animation: fade-in-out 2.4s ease infinite;
      animation-delay: 0.2s;
    }
  }
}

.ios-download {
  background: $ios-download--background;
  position: relative;
  width: $alert-dialog--width;
  font-size: 14px;
  text-align: center;
  margin-top: 12px;
  padding: 10px 0 16px;
}

.ios-download__safari-prompt {
  position: absolute;
  background: rgba($color-rgb--black, 0.3);
  top: -42px;
  left: 0;
  text-align: center;
  width: $alert-dialog--width;
  color: $color-ui--white;
  padding: 8px;
  border-radius: 6px;
  font-size: $font-size--x-small;
  text-shadow: 0px 0px 4px black;
}

.ios-download__icon {
  height: 24px;
  width: 24px;
  position: relative;
  top: 6px;

  &.--add-to-homescreen {
    margin: 0 2px;
    opacity: 0.6;
    top: 7px;
  }
}

@keyframes fade-in-out {
  0% {
    opacity: 0.2;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0.2;
  }
}
</style>
